import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useProfile } from "@/hooks/useAuth";
import { fetchIncomeRecords, deleteIncomeRecordWithTransaction, recalcHouseholdSplit, fetchHouseholdSplitRatio, updateIncomeWindowMonths, INCOME_TABS, incomeSearchValidator, type IncomeRecord, type IncomeTab } from "@/lib/income";
import { fetchMembers } from "@/lib/queries";
import { useFormatCurrency } from "@/hooks/useFormatCurrency";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, TrendingUp, Info, AlertTriangle } from "lucide-react";
import { IncomeDialog } from "@/components/IncomeDialog";
import { IncomePoolSection } from "@/components/IncomePoolSection";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { toastError } from "@/lib/errors";
import { accountsQueryKey, allAccountsQueryKey } from "@/lib/authScopedQueryKeys";

export const Route = createFileRoute("/_app/income")({
  validateSearch: incomeSearchValidator,
  component: IncomePage,
});

const MONTHS = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

function IncomePage() {
  const formatCurrency = useFormatCurrency();
  const { profile } = useProfile();
  const hh = profile?.household_id;
  const profileId = profile?.id;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { tab } = Route.useSearch();
  const [openNew, setOpenNew] = useState(false);

  const incomeQ = useQuery({
    queryKey: ["income-records", hh],
    queryFn: () => fetchIncomeRecords(hh!),
    enabled: !!hh,
  });
  const splitQ = useQuery({
    queryKey: ["household-split", hh],
    queryFn: () => fetchHouseholdSplitRatio(hh!),
    enabled: !!hh,
  });
  const memQ = useQuery({
    queryKey: ["members", hh],
    queryFn: () => fetchMembers(hh!),
    enabled: !!hh,
  });
  const householdQ = useQuery({
    queryKey: ["household", hh],
    queryFn: async () => (await supabase.from("households").select("base_currency").eq("id", hh!).maybeSingle()).data,
    enabled: !!hh,
  });

  const records = incomeQ.data ?? [];
  const split = splitQ.data;
  const members = memQ.data ?? [];
  const memMap = new Map(members.map((m) => [m.id, m.display_name]));
  const baseCurrency = householdQ.data?.base_currency ?? "MXN";

  // Registros en ventana activa que tienen base_currency diferente a la actual (quedan excluidos del ratio)
  const windowMonths = split?.income_window_months ?? 3;
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - windowMonths);
  const hasExcludedRecords = records.some((r) => {
    const recDate = new Date(r.period_year, r.period_month - 1, 1);
    return recDate >= cutoff && r.base_currency !== baseCurrency;
  });

  const isCouple = members.length > 1;
  const memberA = split?.member_a_id ? memMap.get(split.member_a_id) ?? "Miembro A" : "Miembro A";
  const memberB = split?.member_b_id ? memMap.get(split.member_b_id) ?? "Miembro B" : "Miembro B";

  // Agrupar por año-mes para mostrar en secciones
  const grouped = records.reduce<Map<string, typeof records>>((map, r) => {
    const key = `${r.period_year}-${String(r.period_month).padStart(2, "0")}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(r);
    return map;
  }, new Map());
  const sortedGroups = Array.from(grouped.entries()).sort((a, b) => b[0].localeCompare(a[0]));

  async function handleDelete(record: IncomeRecord) {
    try {
      await deleteIncomeRecordWithTransaction(record);
      await recalcHouseholdSplit(record.household_id);
      toast.success("Ingreso eliminado");
      await qc.invalidateQueries({ queryKey: ["income-records"] });
      await qc.invalidateQueries({ queryKey: ["household-split"] });
      if (hh && profileId) {
        await qc.invalidateQueries({ queryKey: accountsQueryKey(hh, profileId), exact: true });
      }
      if (hh && profileId) {
        await qc.invalidateQueries({ queryKey: allAccountsQueryKey(hh, profileId), exact: true });
      }
      await qc.invalidateQueries({ queryKey: ["tx-range"] });
    } catch (e) {
      toastError(e);
    }
  }

  async function handleWindowChange(months: number) {
    if (!hh) return;
    try {
      await updateIncomeWindowMonths(hh, months);
      await recalcHouseholdSplit(hh);
      await qc.invalidateQueries({ queryKey: ["household-split"] });
      toast.success("Ventana actualizada");
    } catch (e) {
      toastError(e);
    }
  }

  const ratioA = split?.split_ratio_a != null ? Math.round(split.split_ratio_a * 100) : 50;
  const ratioB = split?.split_ratio_b != null ? Math.round(split.split_ratio_b * 100) : 50;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Ingresos</h1>
          <p className="text-sm text-muted-foreground">Registrá los sueldos y entradas de dinero para calcular el aporte de cada uno al hogar.</p>
        </div>
        <Button onClick={() => setOpenNew(true)}>
          <Plus className="mr-2 h-4 w-4" /> Registrar ingreso
        </Button>
      </div>

      {/* Card de ratio de aporte */}
      {isCouple && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-4 w-4" />
              Proporción de aportes al hogar
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex-1 space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>{memberA}</span>
                  <span className="font-semibold text-foreground">{ratioA}%</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${ratioA}%` }} />
                </div>
              </div>
              <div className="flex-1 space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>{memberB}</span>
                  <span className="font-semibold text-foreground">{ratioB}%</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: `${ratioB}%` }} />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Info className="h-3 w-3 shrink-0" />
              <span>Calculado sobre los últimos</span>
              <Select
                value={String(split?.income_window_months ?? 3)}
                onValueChange={(v) => handleWindowChange(Number(v))}
              >
                <SelectTrigger className="h-6 w-24 text-xs px-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 mes</SelectItem>
                  <SelectItem value="3">3 meses</SelectItem>
                  <SelectItem value="6">6 meses</SelectItem>
                  <SelectItem value="12">12 meses</SelectItem>
                </SelectContent>
              </Select>
              {split?.split_ratio_updated_at && (
                <span className="ml-auto">
                  Actualizado {new Date(split.split_ratio_updated_at).toLocaleDateString("es-MX")}
                </span>
              )}
            </div>

            {!split?.split_ratio_a && (
              <div className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">
                No hay ingresos registrados en la ventana seleccionada. Se usa 50/50 como proporción por defecto.
              </div>
            )}
            {hasExcludedRecords && (
              <div className="flex items-start gap-2 rounded-md border border-amber-400 bg-amber-100 dark:border-amber-600 dark:bg-amber-900/40 p-2 text-xs text-amber-800 dark:text-amber-300">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                  Hay ingresos en la ventana registrados con otra moneda de referencia y no se incluyen en el cálculo actual ({baseCurrency}). La proporción mostrada es la última calculada y se usará hasta que registres ingresos en {baseCurrency}.
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs
        value={tab}
        onValueChange={(v) => {
          if ((INCOME_TABS as readonly string[]).includes(v)) {
            void navigate({ to: "/income", search: { tab: v as IncomeTab }, replace: true });
          }
        }}
      >
        <TabsList className="grid h-auto w-full grid-cols-2">
          <TabsTrigger value="pozo" className="whitespace-normal px-2 text-xs sm:text-sm">
            Pozo del hogar
          </TabsTrigger>
          <TabsTrigger value="detalle" className="whitespace-normal px-2 text-xs sm:text-sm">
            Detalle de ingresos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pozo" className="space-y-4">
          {/* F5b: pozo de ingresos del hogar (venta de USDT): cada miembro declara su parte del mes */}
          {hh && profileId && (
            <IncomePoolSection householdId={hh} profileId={profileId} members={members} records={records} />
          )}
        </TabsContent>

        <TabsContent value="detalle" className="space-y-4">
          {/* Historial de ingresos agrupado por período */}
          {sortedGroups.length === 0 && !incomeQ.isLoading && (
            <Card>
              <CardContent className="p-10 text-center text-sm text-muted-foreground">
                No hay ingresos registrados todavía. Usá el botón para agregar el primero.
              </CardContent>
            </Card>
          )}

          {sortedGroups.map(([periodKey, items]) => {
            const [yearStr, monthStr] = periodKey.split("-");
            const monthLabel = MONTHS[parseInt(monthStr) - 1];
            const totalByMember = items.reduce<Map<string, number>>((m, r) => {
              m.set(r.member_id, (m.get(r.member_id) ?? 0) + r.amount);
              return m;
            }, new Map());

            return (
              <Card key={periodKey}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    {monthLabel} {yearStr}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {items.map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-2 rounded-md border p-3 text-sm">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{memMap.get(r.member_id) ?? "—"}</span>
                          {r.source_type === "migrated_transaction" && (
                            <Badge variant="secondary" className="text-[10px]">Migrado</Badge>
                          )}
                          {r.pool_account_id && (
                            <Badge variant="secondary" className="text-[10px]">Parte del pozo</Badge>
                          )}
                          {r.base_currency !== baseCurrency && (
                            <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">
                              excluido
                            </Badge>
                          )}
                        </div>
                        {r.notes && <div className="text-xs text-muted-foreground truncate">{r.notes}</div>}
                        {r.currency !== r.base_currency && (
                          <div className="text-xs text-muted-foreground">
                            ≈ {r.amount_base.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {r.base_currency}
                            {r.fx_rate_to_base > 0 && r.fx_rate_to_base !== 1 && (
                              <span> · 1 {r.base_currency} = {(1 / r.fx_rate_to_base).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {r.currency}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-bold tabular-nums">{formatCurrency(r.amount, r.currency)}</span>
                        {r.source_type === "manual" && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() => handleDelete(r)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}

                  {isCouple && totalByMember.size > 1 && (
                    <div className="flex gap-2 pt-1">
                      {Array.from(totalByMember.entries()).map(([memberId, total]) => {
                        const periodTotal = Array.from(totalByMember.values()).reduce((s, v) => s + v, 0);
                        const pct = periodTotal > 0 ? Math.round((total / periodTotal) * 100) : 50;
                        return (
                          <div key={memberId} className="flex-1 rounded-md bg-muted/40 px-3 py-1.5 text-xs">
                            <span className="text-muted-foreground">{memMap.get(memberId) ?? "—"}: </span>
                            <span className="font-semibold">{pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>

      <IncomeDialog open={openNew} onOpenChange={setOpenNew} />
    </div>
  );
}
